"use client";

import { Box, VStack } from "@chakra-ui/react";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import {
  MEILI_SEARCH_CONFIG,
  MemoizedSearchIndex,
  MyInfiniteHitsGeneric,
  SearchQueryConnector,
  usePreservedLoadedPageCount,
} from "@/features/search";
import { PROFILE_SHAPING_EMPTY_STATES } from "@/features/profile-shaping";
import { useMeiliRuntime } from "@/core/search";
import {
  useBacklogSearchControls,
  useMeiliRefreshState,
  useSearchQueryBacklog,
  useShellViewState,
} from "@/state/globalStore";
import { getPrototypeTone } from "@/shared/config/prototypeTheme";
import BacklogOriginSearchHit from "../search/BacklogOriginSearchHit";
import BacklogEmptyState from "./BacklogEmptyState";

type BacklogSourceSearchViewProps = {
  onVisibleOriginIdsChange: (indexId: string, hitIds: string[]) => void;
  onVisibleOriginIdsUnmount: () => void;
};

const ORIGIN_PREVIEW_LENGTH = 200;

export default function BacklogSourceSearchView({
  onVisibleOriginIdsChange,
  onVisibleOriginIdsUnmount,
}: BacklogSourceSearchViewProps) {
  const { meiliRefreshStamp } = useMeiliRefreshState();
  const { searchClient } = useMeiliRuntime({ surface: "backlog" });
  const searchBacklog = useSearchQueryBacklog();
  const backlogControls = useBacklogSearchControls();
  const { activePrototypeCategory } = useShellViewState();
  const activeFrameTone = getPrototypeTone(activePrototypeCategory);
  const originsConfig = MEILI_SEARCH_CONFIG.origins;
  const preserveLoadedPagesKey = [
    "backlog-origins",
    searchBacklog.searchQuery,
    backlogControls.hitsPerPage,
    backlogControls.sortBy,
    backlogControls.semanticRatio,
    backlogControls.filters.language.join(","),
  ].join("|");
  const { loadedPageCount, loadMore } = usePreservedLoadedPageCount(
    preserveLoadedPagesKey,
  );

  if (!searchClient) {
    return null;
  }

  return (
    <InstantSearchNext
      key={meiliRefreshStamp.backlog_origins}
      indexName={originsConfig.indexName}
      searchClient={searchClient}
      routing={false}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      initialUiState={{
        [originsConfig.indexName]: {
          sortBy: originsConfig.sortBy[0].value,
        },
      }}
    >
      <VStack align="stretch">
        <SearchQueryConnector
          query={searchBacklog.searchQuery}
          debounceMs={250}
          debugLabel="backlog"
        />

        <MemoizedSearchIndex
          indexName={originsConfig.indexName}
          indexId="backlog-origins"
          hitsPerPage={backlogControls.hitsPerPage * loadedPageCount}
          mainContentField={originsConfig.mainContentField}
          previewLength={ORIGIN_PREVIEW_LENGTH}
          sortBy={backlogControls.sortBy}
          languageFilters={backlogControls.filters.language}
        >
          <Box
            bg="moveon.surface.bg"
            borderWidth="1"
            borderColor="moveon.surface.border"
            borderTopWidth="3"
            borderTopColor={activeFrameTone.accent}
            borderRadius="md"
            px={{ base: "2", md: "3" }}
            py={{ base: "2", md: "3" }}
            overflow="hidden"
          >
            <MyInfiniteHitsGeneric
              config={originsConfig}
              renderHit={BacklogOriginSearchHit}
              indexId="backlog-origins"
              onIndexHitsChange={onVisibleOriginIdsChange}
              onIndexHitsUnmount={onVisibleOriginIdsUnmount}
              onLoadMoreAction={loadMore}
              listGap={{ base: "2", md: "3" }}
              emptyContent={<BacklogEmptyState />}
              emptyState={{
                ...PROFILE_SHAPING_EMPTY_STATES.backlogSource,
                icon: "origin_source",
              }}
            />
          </Box>
        </MemoizedSearchIndex>
      </VStack>
    </InstantSearchNext>
  );
}
